import { RefObject, useState } from "react";
import { getScrollParent } from "shared/utils/DomUtils";
import { useIsomorphicLayoutEffect } from "usehooks-ts";

export interface ElementRect {
  top: number;
  left: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
}

export const defaultElementRect: ElementRect = {
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  width: 0,
  height: 0,
};

// Based on https://github.com/juliencrn/usehooks-ts/issues/236
export const useElementRect = <T extends HTMLElement = HTMLDivElement>(
  ref: RefObject<T | null | undefined>,
): ElementRect => {
  const [size, setSize] = useState<ElementRect>(defaultElementRect);

  const element = ref.current;
  const scrollParent = element ? getScrollParent(element) : undefined;

  useIsomorphicLayoutEffect(() => {
    const updateSize = (element: Element | null) => {
      const { top, left, right, bottom, width, height } =
        element?.getBoundingClientRect() ?? defaultElementRect;
      setSize({ top, left, right, bottom, width, height });
    };

    if (element) {
      updateSize(element);
    }

    const resizeObserver = new ResizeObserver((entries) => {
      const entry = entries.at(0);
      if (entry) {
        updateSize(entry.target);
      }
    });

    const intersectionObserver = new IntersectionObserver((entries) => {
      const entry = entries.at(0);
      if (entry) {
        updateSize(entry.target);
      }
    });

    const scrollListener = () => {
      if (element) {
        updateSize(element);
      }
    };

    if (element) {
      resizeObserver.observe(element);
      intersectionObserver.observe(element);
      scrollParent?.addEventListener("scroll", scrollListener);
    }

    return () => {
      if (element) {
        resizeObserver.unobserve(element);
        intersectionObserver.unobserve(element);
        scrollParent?.removeEventListener("scroll", scrollListener);
      }
    };
  }, [element]);

  return size;
};
